import { useState } from 'react';
import SeoHead from '../../components/feature/SeoHead';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const faqs = [
  {
    question: 'What exactly does a doula do?',
    answer: 'A doula provides continuous physical, emotional, and informational support before, during, and after childbirth. We help you create a birth plan, guide you through labor with comfort techniques, advocate for your wishes with medical staff, and offer postpartum support as you settle into life with your new baby.'
  },
  {
    question: 'Is a doula the same as a midwife?',
    answer: 'No — a doula and a midwife have different roles. A midwife is a trained medical professional who can deliver your baby and provide clinical care. A doula is a non-medical support person focused entirely on your emotional, physical, and informational needs. We work alongside your midwife or OB to ensure you feel supported throughout your journey.'
  },
  {
    question: 'When should I hire a doula?',
    answer: 'We recommend reaching out as early as possible — ideally during your first or second trimester. This gives us time to build a relationship, understand your birth preferences, and prepare together. That said, we welcome inquiries at any stage of pregnancy and will do our best to accommodate you.'
  },
  {
    question: 'Do you offer virtual doula support?',
    answer: 'Yes! We offer virtual prenatal and postpartum support sessions for clients who are not local to Brooklyn, NY or prefer remote consultations. Virtual support includes birth planning, emotional check-ins, breastfeeding guidance, and newborn care education via video call.'
  },
  {
    question: 'What areas do you serve?',
    answer: 'We are based in Brooklyn, NY and primarily serve the New York City metro area including Brooklyn, Manhattan, Queens, the Bronx, and Staten Island. We also offer virtual support to clients nationwide.'
  },
  {
    question: 'How many prenatal visits are included?',
    answer: 'The number of prenatal visits depends on the package you choose. Our Essential package includes 2 prenatal visits, our Bloom package includes 3, and our Full Bloom package includes unlimited prenatal visits. All packages include a free initial consultation so we can get to know each other first.'
  },
  {
    question: 'What happens if you are unavailable when I go into labor?',
    answer: 'We take your birth support very seriously. In the rare event of an emergency, we have a trusted network of backup doulas who are fully briefed on your birth preferences and will step in seamlessly. You will never be left without support.'
  },
  {
    question: 'Do you support all types of births?',
    answer: 'Absolutely. We support vaginal births, planned and unplanned C-sections, medicated and unmedicated births, VBAC (vaginal birth after cesarean), and hospital, birth center, and home births. Every birth is valid and deserves dedicated support.'
  },
  {
    question: 'How do I get started?',
    answer: 'Simply reach out via the contact form on this page, send us an email at support@lotusmaternal.com, or call us directly at (929) 473-8867. We\'ll schedule a free consultation to discuss your needs, answer any questions, and find the right package for your journey.'
  }
];

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
};

const contactSchema = {
  '@context': 'https://schema.org',
  '@type': 'ContactPage',
  name: 'Contact Lotus Maternal — Brooklyn, NY Doula Services',
  description: 'Get in touch with Lotus Maternal for doula services, questions about packages, or to schedule a free consultation in Brooklyn, NY.',
  url: `${siteUrl}/contact`,
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
      { '@type': 'ListItem', position: 2, name: 'Contact', item: `${siteUrl}/contact` },
    ],
  },
  mainEntity: {
    '@type': 'MedicalBusiness',
    name: 'Lotus Maternal',
    url: siteUrl,
    telephone: '(929)473-8867',
    email: 'support@lotusmaternal.com',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Brooklyn',
      addressRegion: 'NY',
      postalCode: '11201',
      addressCountry: 'US',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 40.6782,
      longitude: -73.9442,
    },
    sameAs: ['https://instagram.com/lotus.maternal'],
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '(929)473-8867',
      email: 'support@lotusmaternal.com',
      contactType: 'customer support',
      availableLanguage: 'English',
      hoursAvailable: {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'],
        opens: '00:00',
        closes: '23:59',
      },
    },
  },
};

function FaqItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-amber-100 last:border-0">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-5 px-2 text-left cursor-pointer group"
      >
        <span className="font-semibold text-gray-800 text-base group-hover:text-amber-700 transition-colors pr-4">
          {question}
        </span>
        <span className={`w-8 h-8 flex items-center justify-center flex-shrink-0 rounded-full transition-all duration-300 ${open ? 'bg-amber-600 text-white rotate-45' : 'bg-amber-100 text-amber-700'}`}>
          <i className="ri-add-line text-lg"></i>
        </span>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ${open ? 'max-h-96 pb-5' : 'max-h-0'}`}>
        <p className="text-gray-600 text-sm leading-relaxed px-2">{answer}</p>
      </div>
    </div>
  );
}

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d6grak35pk3jcsh37d90', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-green-50">
      <SeoHead
        title="Contact Lotus Maternal — Brooklyn, NY Doula Team | (929) 473-8867"
        description="Contact Lotus Maternal for doula services in Brooklyn, NY. Call (929) 473-8867, email support@lotusmaternal.com, or follow @lotus.maternal on Instagram. Available 24/7 for all families."
        keywords="contact doula Brooklyn NY, Lotus Maternal phone number, doula email New York, maternal support contact, doula consultation Brooklyn"
        canonical={`${siteUrl}/contact`}
        ogTitle="Contact Lotus Maternal — Brooklyn, NY Doula Services"
        ogDescription="Reach our doula team in Brooklyn, NY. Call (929) 473-8867 or email support@lotusmaternal.com. Available 24/7 for all families."
        schema={[contactSchema, faqSchema]}
      />
      {/* Hero Section */}
      <div className="relative h-80 overflow-hidden">
        <img
          src="https://public.readdy.ai/ai/img_res/52d1c250fd7562f0b2d03c572c1b3141.jpg"
          alt="Contact us"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-white px-4">
            <h1 className="font-playfair text-5xl md:text-6xl font-bold mb-4">Get In Touch</h1>
            <p className="text-xl md:text-2xl font-light">We're here to answer your questions</p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left Side - Contact Info & Image */}
          <div>
            <div className="mb-8">
              <h2 className="font-playfair text-4xl font-bold text-gray-800 mb-4">Let's Connect</h2>
              <p className="text-gray-600 text-lg leading-relaxed">
                Whether you have questions about our services, want to learn more about our packages, or simply want to say hello, we'd love to hear from you. Reach out and let's start a conversation.
              </p>
            </div>

            {/* Contact Channels - Clearly Separated */}
            <div className="space-y-5 mb-8">

              {/* General Inquiries */}
              <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-amber-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-xl text-white"></i>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-bold text-gray-800 text-base">General Inquiries</h3>
                      <span className="text-xs bg-amber-200 text-amber-800 font-semibold px-2 py-0.5 rounded-full">New Clients Welcome</span>
                    </div>
                    <a href="mailto:info@lotusmaternal.com" className="text-amber-700 hover:text-amber-900 font-semibold text-sm cursor-pointer">
                      info@lotusmaternal.com
                    </a>
                    <p className="text-gray-500 text-xs mt-2 leading-relaxed">
                      Reach out here for general questions, information about our offerings, package inquiries, or if you're just getting started and want to learn more about Lotus Maternal.
                    </p>
                  </div>
                </div>
              </div>

              {/* Divider with label */}
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-gray-200"></div>
                <span className="text-xs text-gray-400 font-medium uppercase tracking-widest">or</span>
                <div className="flex-1 h-px bg-gray-200"></div>
              </div>

              {/* Services & Programs Support */}
              <div className="bg-green-50 border-2 border-green-200 rounded-2xl p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-green-700 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-customer-service-2-line text-xl text-white"></i>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-bold text-gray-800 text-base">Services &amp; Programs Support</h3>
                      <span className="text-xs bg-green-200 text-green-800 font-semibold px-2 py-0.5 rounded-full">Current Clients</span>
                    </div>
                    <a href="mailto:support@lotusmaternal.com" className="text-green-700 hover:text-green-900 font-semibold text-sm cursor-pointer">
                      support@lotusmaternal.com
                    </a>
                    <p className="text-gray-500 text-xs mt-2 leading-relaxed">
                      Already enrolled in a service or program? Contact our support team for help with your active doula package, postpartum program, scheduling, or any ongoing care needs.
                    </p>
                  </div>
                </div>
              </div>

              {/* Phone */}
              <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-5 hover:shadow-lg transition-shadow">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-xl text-gray-600"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-800 mb-1">Call Us</h3>
                    <a href="tel:9294738867" className="text-amber-700 hover:text-amber-800 font-semibold text-sm cursor-pointer">
                      (929) 473-8867
                    </a>
                    <p className="text-gray-500 text-xs mt-1">Available 24/7 for our clients</p>
                  </div>
                </div>
              </div>

              {/* Instagram */}
              <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-5 hover:shadow-lg transition-shadow">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-instagram-line text-xl text-amber-700"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-800 mb-1">Follow Us on Instagram</h3>
                    <a
                      href="https://www.instagram.com/lotus.maternal?igsh=djQxbzN2MjdpNHRv&utm_source=qr"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-amber-700 hover:text-amber-800 font-semibold text-sm cursor-pointer"
                    >
                      @lotus.maternal
                    </a>
                    <p className="text-gray-500 text-xs mt-1">Stay connected &amp; inspired</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Decorative Image */}
            <div className="hidden lg:block rounded-3xl overflow-hidden shadow-2xl">
              <img
                src="https://readdy.ai/api/search-image?query=African%20American%20pregnant%20woman%20sitting%20comfortably%20with%20supportive%20doula%20companion%20in%20cozy%20home%20setting%20warm%20golden%20light%20soft%20cushions%20caring%20nurturing%20moment%20deep%20melanin%20skin%20tones%20natural%20hair%20peaceful%20serene&width=600&height=700&seq=contact-side-fix-2025-v1&orientation=portrait"
                alt="Lotus Maternal support"
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Right Side - Contact Form */}
          <div>
            <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-10">
              <h3 className="font-playfair text-3xl font-bold text-gray-800 mb-2">Send Us a Message</h3>
              <p className="text-gray-600 mb-8">Fill out the form below and we'll get back to you soon</p>

              <form onSubmit={handleSubmit} data-readdy-form id="contact_form">
                <div className="mb-6">
                  <label htmlFor="name" className="block text-gray-700 font-semibold mb-2">
                    Your Name <span className="text-amber-600">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-amber-500 focus:outline-none transition-colors text-sm"
                    placeholder="Your full name"
                  />
                </div>

                <div className="mb-6">
                  <label htmlFor="email" className="block text-gray-700 font-semibold mb-2">
                    Email Address <span className="text-amber-600">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-amber-500 focus:outline-none transition-colors text-sm"
                    placeholder="your.email@example.com"
                  />
                </div>

                <div className="mb-6">
                  <label htmlFor="subject" className="block text-gray-700 font-semibold mb-2">
                    Subject <span className="text-amber-600">*</span>
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-amber-500 focus:outline-none transition-colors text-sm"
                    placeholder="What's this about?"
                  />
                </div>

                <div className="mb-8">
                  <label htmlFor="message" className="block text-gray-700 font-semibold mb-2">
                    Your Message <span className="text-amber-600">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    maxLength={500}
                    rows={6}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-amber-500 focus:outline-none transition-colors resize-none text-sm"
                    placeholder="Tell us more about your inquiry..."
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</p>
                </div>

                {submitStatus === 'success' && (
                  <div className="mb-6 p-4 bg-green-50 border-2 border-green-300 rounded-lg">
                    <p className="text-green-800 font-semibold flex items-center">
                      <i className="ri-checkbox-circle-line text-xl mr-2"></i>
                      Message sent successfully! We'll get back to you soon.
                    </p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="mb-6 p-4 bg-red-100 border-2 border-red-400 rounded-lg">
                    <p className="text-red-800 font-semibold flex items-center">
                      <i className="ri-error-warning-line text-xl mr-2"></i>
                      Something went wrong. Please try again or email us directly.
                    </p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-amber-600 to-green-700 text-white font-bold py-4 px-8 rounded-lg hover:from-amber-700 hover:to-green-800 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mt-24">
          <div className="text-center mb-14">
            <span className="inline-block bg-amber-100 text-amber-700 text-sm font-semibold px-4 py-1.5 rounded-full mb-4 tracking-wide uppercase">
              FAQ
            </span>
            <h2 className="font-playfair text-4xl md:text-5xl font-bold text-gray-800 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-gray-500 text-lg max-w-2xl mx-auto">
              Everything you need to know about our doula services. Can't find your answer? Reach out to us directly.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
            {/* FAQ Accordion */}
            <div className="bg-white rounded-3xl shadow-xl p-8">
              {faqs.slice(0, 5).map((faq, i) => (
                <FaqItem key={i} question={faq.question} answer={faq.answer} />
              ))}
            </div>

            <div className="space-y-8">
              <div className="bg-white rounded-3xl shadow-xl p-8">
                {faqs.slice(5).map((faq, i) => (
                  <FaqItem key={i} question={faq.question} answer={faq.answer} />
                ))}
              </div>

              {/* Still have questions CTA */}
              <div className="bg-gradient-to-br from-amber-600 to-green-700 rounded-3xl p-8 text-white text-center shadow-xl">
                <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-question-answer-line text-3xl text-white"></i>
                </div>
                <h3 className="font-playfair text-2xl font-bold mb-3">Still have questions?</h3>
                <p className="text-white/80 text-sm mb-6 leading-relaxed">
                  We're happy to chat! Book a free consultation, call us, or send us a message and we'll get back to you within 24 hours.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center flex-wrap">
                  <a
                    href="https://calendly.com/queenbriebirthworker/30min"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-white text-amber-700 font-bold px-6 py-3 rounded-full hover:bg-amber-50 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Book Free Consultation
                  </a>
                  <a
                    href="tel:9294738867"
                    className="bg-white/20 border-2 border-white text-white font-bold px-6 py-3 rounded-full hover:bg-white/30 transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
                  >
                    <i className="ri-phone-fill text-sm"></i>
                    (929) 473-8867
                  </a>
                  <a
                    href="mailto:info@lotusmaternal.com"
                    className="border-2 border-white text-white font-bold px-6 py-3 rounded-full hover:bg-white/10 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Email Us
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
